import React, { useState } from "react";
import Logo from '../../assets/Logo.svg';
import camera from '../../assets/camera.svg';
import { useNavigate, Link } from 'react-router-dom';
import { completeProfile } from '../../ApiController/ApiService';
import Swal from 'sweetalert2';

const CreateCompanyProfile = () => {
  const [logo, setLogo] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    companyType: "",
    companyAddress: "",
    city: "",
    state: "",
    country: "",
    companyRegistrationNumber: "",
    email: "",
    mobileNumber: "",
    websiteUrl: "",
    image: null,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(URL.createObjectURL(file));
      setFormData(prev => ({ ...prev, image: file }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" })); // Reset error on typing
  };

const validateForm = () => {
  const newErrors = {};
  if (!formData.fullName) newErrors.fullName = "Company name is required";
  if (!formData.companyType) newErrors.companyType = "Company type is required";
  if (!formData.companyAddress) newErrors.companyAddress = "Company address is required";
  if (!formData.city) newErrors.city = "City is required";
  if (!formData.state) newErrors.state = "State is required";
  if (!formData.country) newErrors.country = "Country is required";
  if (!formData.companyRegistrationNumber) newErrors.companyRegistrationNumber = "Registration number is required";
  
  // Mobile number validation
  if (!formData.mobileNumber) {
    newErrors.mobileNumber = "Business phone number is required";
  } else if (!/^\d+$/.test(formData.mobileNumber)) {
    newErrors.mobileNumber = "Mobile number must contain digits only";
  } else if (formData.mobileNumber.length < 10 || formData.mobileNumber.length > 15) {
    newErrors.mobileNumber = "Mobile number must be between 10 and 15 digits";
  }

  if (!formData.websiteUrl) newErrors.websiteUrl = "Website URL is required";
  if (!formData.image) newErrors.image = "Company logo is required";

  return newErrors;
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      await completeProfile(formData);
      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Company profile saved successfully.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#6F1AFF',
      });
      navigate('/dashboard');
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to save profile',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)',
    }} className="min-h-screen flex items-center justify-center px-4">
      <div className="hidden sm:absolute sm:top-6 sm:left-6 sm:block">
        <img src={Logo} alt="Web Logo" className="w-[139px] mx-auto" />
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 mt-6 mb-6 w-full max-w-md">
        <h2 className="text-center text-xl font-inter font-bold text-[30px] text-[#2D0076] mb-1">
          Create Company Profile
        </h2>
        <p className="text-center font-sans text-[15px] text-gray-500 mb-6">
          Create Your Company Dispatcher Profile
        </p>

        <form className="space-y-3" onSubmit={handleSubmit}>
          {/* Company Name */}
          <div>
            <label className="block text-[14px] text-[#202224] mb-1">Company Name</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              className="w-full border border-[#D8D8D8] rounded-md px-4 py-2 text-sm bg-[#F1F4F9]"
            />
            {errors.fullName && <p className="text-red-500 text-xs">{errors.fullName}</p>}
          </div>

          {/* Company Type */}
          <div>
            <label className="block text-[14px] text-[#202224] mb-1">Company Type</label>
            <input
              type="text"
              name="companyType"
              value={formData.companyType}
              onChange={handleInputChange}
              className="w-full border border-[#D8D8D8] rounded-md px-4 py-2 text-sm bg-[#F1F4F9]"
            />
            {errors.companyType && <p className="text-red-500 text-xs">{errors.companyType}</p>}
          </div>

          {/* Address, City, State */}
          <input
            type="text"
            name="companyAddress"
            placeholder="Company Address"
            value={formData.companyAddress}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm"
          />
          {errors.companyAddress && <p className="text-red-500 text-xs">{errors.companyAddress}</p>}

          <input
            type="text"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm"
          />
          {errors.city && <p className="text-red-500 text-xs">{errors.city}</p>}

          <input
            type="text"
            name="state"
            placeholder="State Province"
            value={formData.state}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm"
          />
          {errors.state && <p className="text-red-500 text-xs">{errors.state}</p>}

          {/* Country */}
          <div>
            <label className="block text-[14px] text-[#202224] mb-1">Country</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className="w-full border border-[#D8D8D8] rounded-md px-4 py-2 text-sm bg-[#F1F4F9]"
            />
            {errors.country && <p className="text-red-500 text-xs">{errors.country}</p>}
          </div>

          {/* Registration Number */}
          <div>
            <label className="block text-[14px] text-[#202224] mb-1">Company Registration Number</label>
            <input
              type="text"
              name="companyRegistrationNumber"
              value={formData.companyRegistrationNumber}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm"
            />
            {errors.companyRegistrationNumber && <p className="text-red-500 text-xs">{errors.companyRegistrationNumber}</p>}
          </div>

          {/* Mobile Number */}
          <div>
            <label className="block text-[14px] text-[#202224] mb-1">Business Phone Number</label>
            <input
              type="text"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm"
            />
            {errors.mobileNumber && <p className="text-red-500 text-xs">{errors.mobileNumber}</p>}
          </div>

          {/* Website */}
          <div>
            <label className="block text-[14px] text-[#202224] mb-1">Website URL</label>
            <input
              type="text"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm"
            />
            {errors.websiteUrl && <p className="text-red-500 text-xs">{errors.websiteUrl}</p>}
          </div>

          {/* Logo Upload */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Upload Company Logo</p>
            <div className="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center w-64 h-56">
              {logo ? (
                <img src={logo} alt="Company Logo" className="w-24 h-24 object-cover" />
              ) : (
                <div className="flex flex-col items-center space-y-3">
                  <div><img src={camera} alt="Camera icon" /></div>
                  <input
                    type="file"
                    onChange={handleLogoChange}
                    className="hidden"
                    id="upload"
                    accept="image/*"
                  />
                  <label htmlFor="upload" className="bg-[#797979] text-white px-4 py-1 rounded-md text-sm cursor-pointer hover:bg-gray-600">
                    Upload Image
                  </label>
                </div>
              )}
            </div>
            {errors.image && <p className="text-red-500 text-xs">{errors.image}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-[#6F1AFF] text-white py-2 rounded-md font-semibold text-sm hover:bg-[#6939e8] ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLoading ? 'Saving...' : 'Save Profile'}
          </button>

          {/* <p className="text-[#00217A] text-center text-[20px] mt-3">
            <Link to="/team-management" className="text-[#00217A] text-[20px] font-bold hover:underline">Skip</Link>
          </p> */}
        </form>
      </div>
    </div>
  );
};

export default CreateCompanyProfile;
